import { ref, computed } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'
import { shouldApplySplitCaseSurcharge as productShouldApplySplitCaseSurcharge, normalizeQuantityForProduct } from '@/Composables/UseProductQuantity'
import { computeOrderTotals } from '@/Composables/useOrderTotals'

const state = ref({
    lastUserFetched: null,
    cart: [],
    user: null,
    tax_exempt: false,
    isFetching: false,
    showCartError: false,
});

export default function UseCart (user, tax_exempt) {
    state.value.user = user;
    state.value.tax_exempt = tax_exempt;

    const showCartError = function () {
        console.log('showCartError called');
        state.value.showCartError = true;
    };

    const hideCartError = function () {
        state.value.showCartError = false;
    };

    const fetchCart = async function () {
        try {
            state.value.isFetching = true;
            const response = await axios.get(route('cart'));
            state.value.cart = response.data;
            state.value.isFetching = false;
        } catch (error) {
            state.value.lastUserFetched = null;
        }
    };

    const saveCart = _.debounce(function () {
        axios.post(route('cart'), {
            items: { ...state.value.cart },
        }).then((response) => {
            if (response.data !== 'success' && response.request.responseURL) {
                window.location = response.request.responseURL;
            }
        }).catch(error => {
            console.log('Cart save error:', error);
            
            // Send error to backend for logging
            axios.post(route('cart.log_error'), {
                error_message: error.message || 'Unknown error',
                error_stack: error.stack || '',
                error_name: error.name || 'Error',
                error_code: error.code || null,
                error_status: error.response?.status || null,
                error_status_text: error.response?.statusText || null,
                error_url: error.config?.url || null,
                error_method: error.config?.method || null,
                error_headers: error.config?.headers ? JSON.stringify(error.config.headers) : null,
                error_data: error.config?.data || null,
                error_response_data: error.response?.data ? JSON.stringify(error.response.data) : null,
                cart_data: JSON.stringify(state.value.cart),
                cart_count: state.value.cart.length,
                user_id: state.value.user?.id || null,
                timestamp: new Date().toISOString(),
                user_agent: navigator.userAgent,
                current_url: window.location.href,
            }).catch(logError => {
                console.log('Failed to log cart error:', logError);
            });
            
            // Show error message to user
            showCartError();
        });
    }, 250);

    const shouldApplySplitCaseSurcharge = function (quantity, product) {
        const activeCustomer = usePage().props.value.activeCustomer;

        return productShouldApplySplitCaseSurcharge(quantity, activeCustomer, product);
    };

    // Select unit price from Pricing service values when split-case surcharge applies.
    const effectiveUnitPrice = function (product, quantity) {
        if (shouldApplySplitCaseSurcharge(quantity, product) && product.split_case_price != null) return product.split_case_price;
        return product.price != null ? product.price : 0;
    };

    const updateCart = function (product, quantity, log = false) {
        quantity = normalizeQuantityForProduct(quantity, product);

        // If the quantity is greater than 0, add the item to the cart
        if (quantity > 0) {
            const cartItem = state.value.cart.find(item => item.product.id === product.id);

            if (cartItem) {
                cartItem.quantity = quantity;
                cartItem.price = effectiveUnitPrice(cartItem.product, quantity);
            } else {
                state.value.cart.push({
                    quantity,
                    product,
                    price: effectiveUnitPrice(product, quantity),
                });
            }
        } else {
            const cartIndex = state.value.cart.findIndex(item => item.product.id === product.id);

            // If the item is in the cart, remove it
            if (cartIndex !== -1) {
                state.value.cart.splice(cartIndex, 1);

                // Log the deleted item if log is true
                if (log) {
                    // Send to backend for logging
                    axios.post(route('cart.log_checkout_removal'), {
                        product_id: product.id,
                    }).catch(logError => {
                        console.log('Failed to log cart removal:', logError);
                    });
                }
            }
        }

        saveCart();
    };

    const emptyCart = () => {
        state.value.cart.splice(0, state.value.cart.length);

        saveCart();
    };

    // Use same order-totals logic as backend (per-line rounding, no promo in cart)
    const cartOrderTotals = computed(() => {
        const cart = state.value.cart;
        const virtualOrder = {
            order_items: cart.map(item => ({
                id: item.product?.id,
                price: item.price,
                quantity: item.quantity,
                tax: !!(item.product?.taxable)
            })),
            delivery_fee: 0,
            fee_for_delivery: 0,
            tax_exempt: null,
            customer: { tax_exempt: state.value.tax_exempt },
            applied_credit_amount: 0
        };
        return computeOrderTotals(virtualOrder);
    });

    const cartSubtotal = computed(() => cartOrderTotals.value.subtotal);
    const cartTax = computed(() => cartOrderTotals.value.tax);
    const cartTotal = computed(() => cartOrderTotals.value.total);

    const cartCount = computed(() => state.value.cart.reduce((carry, item) => carry += item.quantity, 0));

    const cartHasSpecialOrderItems = computed(() => state.value.cart.filter(item => item.product.special_order === true).length > 0 ? true : false);

    const cartCategories = computed(() => new Set(state.value.cart.map(c => { return c.product.category_id})));

    return {
        isFetching: computed(() => state.value.isFetching),
        cart: computed(() => state.value.cart),
        cartSubtotal,
        cartTax,
        cartTotal,
        cartCount,
        cartHasSpecialOrderItems,
        cartCategories,
        showCartError: computed(() => state.value.showCartError),
        updateCart,
        emptyCart,
        fetchCart,
        hideCartError,
    }
}
